// PARTS-SERVING-1 (2026-10-06): open a hotspot sheet link (`links[].page_id`)
// in the overlay viewer.
//
// There is no page-flipping inside the diagram viewer itself: it renders ONE
// overlay page, taken from its dock tab (`artifact.data.page ?? artifact.page`),
// and loads it through its own `/v1/bot/diagram/{page}/image|hotspots` fetch.
// Showing another sheet therefore = showing another dock tab — the same
// mechanism the comp-ref chips use to bring a sheet into view
// (`openArtifact`). No new endpoint and no new fetch path: the viewer keyed
// by the new tab id fetches the linked page exactly like any other page,
// under the same proxy entitlement.
//
//   1. A tab already shows that page (any diagram_overlay tab, matched by the
//      comp-ref join `diagramOverlayPageOf`) -> activate it.
//   2. Otherwise -> `openArtifact` with a diagram_overlay artifact for the
//      page, cloned from the CURRENT sheet's artifact (same source /
//      document / collection — the contract guarantees a link stays inside
//      the same document), with no answer highlights (`components: []`) and
//      an empty url (the bot path is not a SPA download url).
//   3. In V3 fullscreen the overlay chrome follows `fullscreenArtifactId`, so
//      it is moved to the tab that is now active (the fullscreen body shows
//      the active tab) — otherwise title and body would disagree.

import type { AnyAction } from "@reduxjs/toolkit";
import { Artifact, ArtifactTab } from "./midasPageModels";
import { openArtifact, setActiveArtifactTab } from "./midasPageSlice";
import { setFullscreenArtifactId } from "./midasV3Slice";
import { diagramOverlayPageOf } from "./utils/compRefFlash";

/** The slice of RootState this thunk reads (structural — no store import). */
export interface DiagramSheetNavState {
  midasPage: { artifactTabs: ArtifactTab[]; activeArtifactTabId: string | null };
  midasV3: { fullscreenArtifactId: string | null };
}

export interface OpenDiagramSheetArgs {
  /** The artifact of the sheet whose popup carried the link. */
  fromArtifact: Artifact;
  /** `links[].page_id` — already validated (positive integer). */
  page: number;
  /** Tab title for a newly opened sheet (localized by the caller). */
  title: string;
}

/** Pure: the diagram_overlay artifact for a linked sheet. */
export const buildDiagramSheetArtifact = ({
  fromArtifact,
  page,
  title,
}: OpenDiagramSheetArgs): Artifact => {
  const fromData = (fromArtifact.data || {}) as Record<string, unknown>;
  const data: Record<string, unknown> = {
    page,
    components: [],
    image_url: "",
    hotspots_url: "",
  };
  if (typeof fromData.collection === "string") data.collection = fromData.collection;
  return {
    type: "diagram_overlay",
    title,
    source: fromArtifact.source,
    url: "",
    document_id: fromArtifact.document_id,
    page,
    data,
  };
};

export const openDiagramSheet =
  (args: OpenDiagramSheetArgs) =>
  (dispatch: (action: AnyAction) => unknown, getState: () => DiagramSheetNavState): void => {
    const { page } = args;
    if (!Number.isSafeInteger(page) || page <= 0) return;

    const existing = getState().midasPage.artifactTabs.find(
      (t) => diagramOverlayPageOf(t.artifact) === page
    );
    if (existing) {
      dispatch(setActiveArtifactTab(existing.id));
    } else {
      dispatch(openArtifact(buildDiagramSheetArtifact(args)));
    }

    const after = getState();
    const activeId = after.midasPage.activeArtifactTabId;
    if (after.midasV3.fullscreenArtifactId && activeId) {
      dispatch(setFullscreenArtifactId(activeId));
    }
  };
